import { ceilToUnit, chipUnitOf, floorToUnit, sizedAmountProblem } from './chips.js';
import { DomainError } from './errors.js';
import { isBettingOver } from './hand.js';
import type { Award, Hand, HandEvent, LegalAction, PlayerAction, Pt, SeatNo } from './types.js';

/**
 * 実行可能アクションの算出と、要求されたアクションの検証（design 6.4）。
 *
 * ベッティングルールはここだけに置く。クライアントは `legalActions` の結果を描画する
 * だけの層に保ち、サーバのアクション検証も同じ結果との照合で行う。判定ロジックを
 * 2箇所に持たない。
 */

/**
 * 指定席が今取れるアクションとその金額範囲（要件6.1〜6.9）。
 *
 * 手番でない席、参加していない席には空配列を返す。
 */
export function legalActions(hand: Hand, seatNo: SeatNo): LegalAction[] {
  if (isBettingOver(hand.street)) return [];
  if (hand.currentTurn !== seatNo) return [];

  const seat = hand.seats[seatNo];
  if (seat === undefined || seat.status !== 'ACTIVE') return [];

  const toCall = hand.currentBet - seat.streetContributed;
  /** オールインしたときの到達額。 */
  const maxTo = seat.streetContributed + seat.stack;
  const out: LegalAction[] = [];

  // チェックできる場面でフォールドは提示しない（誤操作防止）。
  if (toCall > 0) out.push({ type: 'FOLD' });

  if (toCall === 0) {
    out.push({ type: 'CHECK' }); // 要件6.3
  } else if (seat.stack > toCall) {
    out.push({ type: 'CALL', amount: toCall }); // 要件6.4
  }
  // stack <= toCall のときコールは ALLIN に吸収される（要件6.5）。

  const step = chipUnitOf(hand.init.config);

  if (hand.currentBet === 0) {
    // 最小ベット額はビッグブラインド額（要件6.6）。スタックが足りなければ全額。
    const minTo = Math.min(ceilToUnit(hand.init.config.bigBlind, step), maxTo);
    if (maxTo > 0 && maxTo > minTo) {
      out.push({ type: 'BET', minTo, maxTo, step });
    }
  } else {
    const minTo = ceilToUnit(minRaiseTo(hand), step);
    if (maxTo > minTo) {
      out.push({ type: 'RAISE', minTo, maxTo, step });
    }
  }

  // オールインは常に可能（要件6.8）。最小レイズに届かない場合もここだけが残る。
  if (seat.stack > 0) out.push({ type: 'ALLIN', to: maxTo });

  return out;
}

/**
 * 最小レイズの到達額（要件6.7）。
 *
 * 現在のベット額の2倍以上とする。標準ルールの「直前の増分を上乗せ」より常に大きいか
 * 等しいが、ビッグブラインドが全額に満たずオールインした場面などでは2倍が増分ルールを
 * 下回ることがあるため、両方の大きい方を取る。2倍ルールを採っても、増分ルールより緩く
 * なる場面は作らない。
 */
export function minRaiseTo(hand: Hand): Pt {
  return Math.max(hand.currentBet * 2, hand.currentBet + hand.lastRaiseSize);
}

function find<T extends LegalAction['type']>(
  actions: readonly LegalAction[],
  type: T,
): Extract<LegalAction, { type: T }> | undefined {
  return actions.find((a) => a.type === type) as Extract<LegalAction, { type: T }> | undefined;
}

/**
 * 要求されたアクションを検証し、追記するイベントへ変換する。
 *
 * 判定は `legalActions` の結果との照合のみで行う。
 */
export function validateAction(hand: Hand, seatNo: SeatNo, action: PlayerAction): HandEvent {
  if (isBettingOver(hand.street)) {
    throw new DomainError('ILLEGAL_ACTION', 'ベッティングは終了しています');
  }
  if (hand.currentTurn !== seatNo) {
    throw new DomainError('NOT_YOUR_TURN', 'この席の手番ではありません');
  }

  const legal = legalActions(hand, seatNo);
  const seq = hand.events.length;

  switch (action.type) {
    case 'FOLD': {
      if (!find(legal, 'FOLD')) throw new DomainError('ILLEGAL_ACTION', 'フォールドできません');
      return { seq, type: 'FOLD', seatNo };
    }
    case 'CHECK': {
      if (!find(legal, 'CHECK')) throw new DomainError('ILLEGAL_ACTION', 'チェックできません');
      return { seq, type: 'CHECK', seatNo };
    }
    case 'CALL': {
      if (!find(legal, 'CALL')) throw new DomainError('ILLEGAL_ACTION', 'コールできません');
      return { seq, type: 'CALL', seatNo };
    }
    case 'BET': {
      const spec = find(legal, 'BET');
      if (!spec) throw new DomainError('ILLEGAL_ACTION', 'ベットできません');
      assertSized(spec, action.to, 'ベット額');
      return { seq, type: 'BET', seatNo, to: action.to };
    }
    case 'RAISE': {
      const spec = find(legal, 'RAISE');
      if (!spec) throw new DomainError('ILLEGAL_ACTION', 'レイズできません');
      assertSized(spec, action.to, 'レイズ額');
      return { seq, type: 'RAISE', seatNo, to: action.to };
    }
    case 'ALLIN': {
      const spec = find(legal, 'ALLIN');
      if (!spec) throw new DomainError('ILLEGAL_ACTION', 'オールインできません');
      if (action.to !== spec.to) {
        throw new DomainError('ILLEGAL_ACTION', 'オールイン額が一致しません');
      }
      return { seq, type: 'ALLIN', seatNo, to: action.to };
    }
  }
}

/** クライアントがボタンの可否に使うのと同じ判定で検証する。 */
function assertSized(spec: { minTo: Pt; maxTo: Pt; step: Pt }, to: Pt, label: string): void {
  const problem = sizedAmountProblem(spec, to);
  if (problem !== null) throw new DomainError('ILLEGAL_ACTION', `${label}: ${problem}`);
}

/**
 * ショーダウンでの勝者入力を検証する（要件9.2、9.4）。
 *
 * すべてのポットに勝者が指定され、勝者が全員そのポットの獲得資格を持つことを要求する。
 */
export function validateAwards(hand: Hand, awards: readonly Award[]): HandEvent {
  if (hand.street !== 'SHOWDOWN') {
    throw new DomainError('ILLEGAL_ACTION', 'ショーダウンではありません');
  }

  const byIndex = new Map<number, Award>();
  for (const award of awards) {
    if (byIndex.has(award.potIndex)) {
      throw new DomainError('BAD_REQUEST', `ポット ${award.potIndex} が重複しています`);
    }
    byIndex.set(award.potIndex, award);
  }

  const normalized: Award[] = [];
  for (let i = 0; i < hand.pots.length; i += 1) {
    const award = byIndex.get(i);
    if (award === undefined || award.winners.length === 0) {
      throw new DomainError('AWARD_INCOMPLETE', `ポット ${i} の勝者が未入力`);
    }
    const pot = hand.pots[i];
    if (pot === undefined) throw new DomainError('INTERNAL', 'ポットが存在しない');

    const winners = [...new Set(award.winners)].sort((a, b) => a - b);
    for (const w of winners) {
      if (!pot.eligible.includes(w)) {
        throw new DomainError('ILLEGAL_ACTION', `席 ${w} はポット ${i} の獲得資格を持ちません`);
      }
    }
    normalized.push({ potIndex: i, winners });
  }

  if (byIndex.size !== hand.pots.length) {
    throw new DomainError('BAD_REQUEST', '存在しないポットが指定されています');
  }

  return { seq: hand.events.length, type: 'AWARD', awards: normalized };
}

export type BetPreset = { label: string; to: Pt };

/**
 * ベット / レイズ額のプリセット（要件6.10）。
 *
 * - ベット（誰もまだ賭けていないストリート）はポット額を基準にする: min / 1/2 / Pot / ALL
 * - レイズは現在のベット額の倍率で選ぶ: 2x / 2.5x / 3x / ALL
 *
 * レイズをポット基準にすると、相手のベット額と無関係な半端な額になり、卓で口頭で
 * 言いにくい。倍率なら「3倍」で通じる。
 *
 * 範囲と単位はサーバが算出した `LegalAction` から受け取る。ここは目安を作るだけで、
 * 判定は行わない。どのプリセットも `isValidSizedAmount` を満たす額に丸める（単位へ
 * 切り下げてから範囲に収める。範囲の上端は全額なので単位の倍数でなくてよい）。
 *
 * ラベルは短い英字にする。横一列に並ぶうえ、押すと金額入力に実額が出るので、ボタンで
 * 説明する必要がない。
 */
export function betPresets(
  spec: Extract<LegalAction, { type: 'BET' | 'RAISE' }>,
  ctx: { potTotal: Pt; currentBet: Pt },
): BetPreset[] {
  const candidates: BetPreset[] =
    spec.type === 'BET'
      ? [
          { label: 'min', to: spec.minTo },
          { label: '1/2', to: Math.floor(ctx.potTotal / 2) },
          { label: 'Pot', to: ctx.potTotal },
          { label: 'ALL', to: spec.maxTo },
        ]
      : [
          { label: '2x', to: ctx.currentBet * 2 },
          { label: '2.5x', to: Math.floor((ctx.currentBet * 5) / 2) },
          { label: '3x', to: ctx.currentBet * 3 },
          { label: 'ALL', to: spec.maxTo },
        ];

  const seen = new Set<Pt>();
  const out: BetPreset[] = [];
  for (const c of candidates) {
    const rounded = c.to === spec.maxTo ? c.to : floorToUnit(c.to, spec.step);
    const to = Math.min(Math.max(rounded, spec.minTo), spec.maxTo);
    if (seen.has(to)) continue;
    seen.add(to);
    out.push({ label: c.label, to });
  }
  return out;
}
